/**
 * Allys Theme Locale Switcher
 * 
 * Custom locale switcher with dropdown matching currency switcher style
 * Supports English and French languages
 * 
 * Works correctly in both desktop (portal-based) and mobile menu (inline) contexts
 */

'use client';

import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { LocaleSwitcherProps } from '@routeroyal/theme-core';

interface Locale {
  code: string;
  name: string;
}

const SUPPORTED_LOCALES: Locale[] = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
];

/**
 * Allys Locale Switcher
 * Custom implementation matching currency switcher style
 */
export function AllysLocaleSwitcher({ locale, className, inMobileMenu = false }: LocaleSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Use ref for button rect to avoid state timing issues
  const buttonRectRef = useRef<DOMRect | null>(null);
  const [, forceUpdate] = useState(0);
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  
  const currentLocaleData = SUPPORTED_LOCALES.find(l => l.code === locale) ?? SUPPORTED_LOCALES[0] ?? { code: 'en', name: 'English' };
  const otherLocales = SUPPORTED_LOCALES.filter(l => l.code !== locale);

  // Build the target path by replacing the locale in the current path
  const getLocalizedPath = useCallback((targetLocale: string) => {
    const pathSegments = pathname.split('/').filter(Boolean);
    
    // If the first segment is a locale, replace it
    if (pathSegments.length > 0 && SUPPORTED_LOCALES.some(l => l.code === pathSegments[0])) {
      pathSegments[0] = targetLocale;
      return '/' + pathSegments.join('/');
    }
    
    // Otherwise, just prepend the locale
    return `/${targetLocale}${pathname === '/' ? '' : pathname}`;
  }, [pathname]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current && !inMobileMenu) {
      buttonRectRef.current = buttonRef.current.getBoundingClientRect();
      forceUpdate(n => n + 1);
    }
  }, [isOpen, inMobileMenu]);

  // Handle click outside for inline dropdown
  useEffect(() => {
    if (!isOpen || !inMobileMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, inMobileMenu]);

  // Dropdown content (shared between portal and inline rendering)
  const dropdownContent = (
    <div className="py-2">
      {otherLocales.map((localeOption) => (
        <Link
          key={localeOption.code}
          href={getLocalizedPath(localeOption.code)}
          onClick={() => setIsOpen(false)}
          className="w-full px-4 py-3 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors duration-200 flex items-center justify-between [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] block min-h-[48px] touch-manipulation"
        >
          <div className="flex items-center gap-3">
            <div className="flex flex-col">
              <span className="font-semibold text-gray-900 text-sm">{localeOption.code.toUpperCase()}</span>
              <span className="text-xs text-gray-500">{localeOption.name}</span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );

  return (
    <div className={`relative ${className || ''}`} ref={dropdownRef}>
      {/* Locale Button */}
      <button
        ref={buttonRef}
        onClick={() => {
          // Set buttonRect ref immediately before toggling isOpen to ensure portal renders
          if (!isOpen && buttonRef.current && !inMobileMenu) {
            buttonRectRef.current = buttonRef.current.getBoundingClientRect();
          }
          setIsOpen(!isOpen);
        }}
        className={`inline-flex items-center gap-2 rounded-lg bg-transparent border-0 text-gray-700 hover:text-gray-900 hover:bg-gray-100/80 active:bg-gray-200/80 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 font-medium text-sm [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] touch-manipulation ${
          inMobileMenu 
            ? 'px-4 py-3 min-h-[48px] min-w-[80px] justify-center' 
            : 'px-5 py-2'
        }`}
        aria-label={`${currentLocaleData.code.toUpperCase()}: Select language`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>{currentLocaleData.code.toUpperCase()}</span>
        <motion.svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </motion.svg>
      </button>

      {/* Mobile Dropdown (Inline) */}
      {inMobileMenu && (
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute left-1/2 -translate-x-1/2 top-full mt-2 min-w-[160px] bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-[100]"
              role="listbox"
            >
              {dropdownContent}
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Desktop Dropdown (Portal) */}
      {!inMobileMenu && mounted && createPortal(
        isOpen && buttonRectRef.current ? (
          <AnimatePresence>
            <motion.div
              key="locale-dropdown"
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'fixed',
                top: buttonRectRef.current.bottom + 8,
                right: window.innerWidth - buttonRectRef.current.right,
              }}
              className="min-w-[180px] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-[10000]"
              role="listbox"
            >
              {dropdownContent}
            </motion.div>
            {/* Backdrop */}
            <motion.div
              key="locale-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999]"
              onClick={() => setIsOpen(false)}
              aria-hidden="true"
            />
          </AnimatePresence>
        ) : null,
        document.body
      )}
    </div>
  );
}

